import type { Entries, SelectItem } from "~/types";

export function mbToBytes(mb: number) {
  return mb * 1024 * 1024;
}

export function buildIncrementalArray(length: number, start = 0) {
  return buildArray(length).map((_, i) => i + start);
}

export function resizeArrayOfObject<T>(original: T[], length: number, factory: () => T): T[] {
  let array: T[];
  if (length > original.length) {
    array = [];
    for (let i = 0; i < length; i++) {
      array.push(original[i] || factory());
    }
  } else {
    array = [...original];
    array.splice(length);
  }
  return array;
}

export function toSelectItems<K extends string>(keyToLabel: Record<K, string>): SelectItem<K>[] {
  return objectEntries(keyToLabel).map(([key, label]) => {
    return { title: label, value: key };
  });
}

export function inViewport(el: HTMLElement) {
  const { top, bottom } = el.getBoundingClientRect();
  return top > 0 && bottom < window.innerHeight;
}

export function objectKeys<T extends object>(obj: T) {
  return Object.keys(obj) as (keyof typeof obj)[];
}

export function isNumber(value: any) {
  return !isNaN(value);
}

export function includes<T extends U, U>(arr: ReadonlyArray<T>, searchElement: U): searchElement is T {
  return arr.includes(searchElement as T);
}

export function isVNodeHidden({ type }: VNode) {
  return type.toString() == 'Symbol(v-cmt)';
}

export function sortArray<T>(array: Readonly<T[]>, fn: (a: T, b: T) => number) {
  return [...array].sort(fn);
}

export function buildArray<T = number>(length: number, value = 0 as T) {
  return new Array<T>(length).fill(value);
}

export function extractFilename(filename: string) {
  return filename.split('.')[0];
}

export function waitForElement(selector: string) {
  return new Promise<HTMLElement | null>(resolve => {
    if (document.querySelector(selector)) {
      return resolve(document.querySelector<HTMLElement>(selector));
    }
    const observer = new MutationObserver(() => {
      if (document.querySelector<HTMLElement>(selector)) {
        observer.disconnect();
        resolve(document.querySelector<HTMLElement>(selector));
      }
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  });
}

export function chunkString(str: string, size: number) {
  return str.match(new RegExp(`.{1,${size}}`, 'g'));
}

export function splitArray<T>(array: T[], size: number) {
  return Array.from({ length: Math.ceil(array.length / size) }, (_, i) => 
    array.slice(i * size, (i + 1) * size)
  );
}

export function objectEntries<T extends object>(obj: T) {
  return Object.entries(obj) as Entries<T>;
}

export function omitFalsyProps<T extends object>(obj: T) {
  return Object.fromEntries(objectEntries(obj).filter(([_, val]) => !!val));
}

export function groupBy<T, K extends keyof any>(list: T[], keyGetter: (obj: T) => K): Record<K, T[]> {
  const map = {} as Record<K, T[]>;
  list.forEach(item => {
    const key = keyGetter(item);
    if (!map[key]) map[key] = [item];
    else map[key].push(item);
  });
  return map;
}